import React from 'react';

import cx from 'classnames';
import * as Draft from 'draft-js';
import { Sparkle } from 'phosphor-react';

import Button from 'components/shared/Button';
import Text from 'components/shared/Typography/Text';

import { OnChangeState } from '../Wysiwyg/Wysiwyg.types';
import AIFeedbackBar from './AIFeedbackBar';
import { AIPromptEditor } from './AIPromptEditor';
import s from './AIWysiwygWrapper.module.css';
import * as t from './AIWysiwygWrapper.types';
import { useLandingTextBlockGeneration } from './useLandingTextBlockGeneration';

export const AIWysiwygWrapper = (props: t.Props) => {
  const textBlockGeneration = useLandingTextBlockGeneration(props);
  const promptEditorRef = React.useRef<Draft.Editor>(null);
  const [isEmptyText, setIsEmptyText] = React.useState(() => {
    if (typeof props.data === 'string' && props.data.length > 0) {
      return false;
    }

    if (!!props.data && typeof props.data === 'object') {
      if (props.data.text || props.data.raw) {
        return false;
      }
    }

    return true;
  });

  const wrapClasses = cx('custom-text-wrapper');
  const WysiwygFooter = () => {
    if (!isEmptyText) {
      return null;
    }

    return (
      <div className={s.footer}>
        <Button
          shape="ellipse"
          type="default"
          size="small"
          onClick={textBlockGeneration.showPromptEditor}
          className={s.aiButton}
        >
          <Sparkle size={16} />
          <Text type="brandon" size="medium">
            Ask AI
          </Text>
        </Button>
      </div>
    );
  };

  if (textBlockGeneration.isShowPromptEditor) {
    return (
      <div className={wrapClasses}>
        <AIPromptEditor
          anchorRef={promptEditorRef}
          loading={textBlockGeneration.isLoading}
          onSubmit={textBlockGeneration.onGenerate}
          onCancel={textBlockGeneration.onCancel}
          onBlur={textBlockGeneration.hidePromptEditor}
        />
      </div>
    );
  }

  const handleKeyBindingFn = (e: React.KeyboardEvent, editorState: Draft.EditorState) => {
    if (e.key === '/' && editorState.getCurrentContent().getPlainText().length === 0) {
      return 'open-ai';
    }

    return null;
  };

  const handleKeyCommand = (command: string) => {
    if (command === 'open-ai') {
      textBlockGeneration.showPromptEditor();
      setTimeout(() => {
        promptEditorRef.current?.focus();
      });
      return 'handled';
    }

    return 'not-handled';
  };

  const onChange = (state: OnChangeState) => {
    const isEmptyText = !state.data.text ? true : state.data.text?.trim?.()?.length < 1;
    setIsEmptyText(isEmptyText);
  };

  return (
    <div className={wrapClasses}>
      {props.children({ onChange, handleKeyBindingFn, handleKeyCommand, WysiwygFooter })}
      {textBlockGeneration.isSuccess && (
        <AIFeedbackBar
          key={textBlockGeneration.generationData?.id}
          loading={textBlockGeneration.isLoading}
          onThumbsUp={() => textBlockGeneration.onRate('positive')}
          onThumbsDown={() => textBlockGeneration.onRate('negative')}
          onTryAgain={textBlockGeneration.onRetry}
        />
      )}
    </div>
  );
};
